/**
 * InstallHubAppButton — v3.21-1803
 *
 * Permanent footer entry point to install the Prestigious Hub PWA.
 * Solves the case where the owner missed the browser's initial
 * install prompt and had no in-Hub way to try again.
 *
 * Behavior:
 *   · If the runtime is already installed (`display-mode:standalone`
 *     or iOS `navigator.standalone`) → the button is HIDDEN.
 *   · If a `BeforeInstallPromptEvent` is currently held (Chromium /
 *     Android / desktop Chromium) → click invokes the native prompt.
 *   · Otherwise → click opens a compact platform-specific
 *     instructions dialog (iOS Share → Add to Home Screen; Android
 *     menu → Install app; desktop browser address bar / menu).
 *
 * A dismissed native prompt NEVER marks the app installed and NEVER
 * hides the button.  Installation truth is derived exclusively from
 * the browser (display-mode / navigator.standalone / appinstalled).
 */
import { useState } from 'react';
import { Download, X as XIcon, Share2, MoreVertical } from 'lucide-react';
import usePwaInstall from '../hooks/usePwaInstall';

export default function InstallHubAppButton({ className = '' }) {
  const { canPrompt, isInstalled, platform, promptInstall } = usePwaInstall();
  const [showHelp, setShowHelp] = useState(false);

  if (isInstalled) return null;

  const onClick = async () => {
    if (canPrompt) {
      const r = await promptInstall();
      // 'accepted' → appinstalled fires and the button disappears via
      // the hook.  'dismissed' → keep the button visible; the browser
      // will typically re-fire beforeinstallprompt later.
      // 'unavailable' → fall back to manual instructions.
      if (r.outcome !== 'accepted') {
        if (r.outcome === 'unavailable') setShowHelp(true);
      }
      return;
    }
    setShowHelp(true);
  };

  return (
    <>
      <button
        type="button"
        onClick={onClick}
        data-testid="footer-install-hub-app-btn"
        className={
          'inline-flex items-center gap-1.5 h-7 px-3 rounded-full '
          + 'border border-gold/60 bg-slate-900 text-[11px] uppercase '
          + 'tracking-[0.2em] text-gold hover:bg-slate-800 '
          + 'hover:text-white transition-colors '
          + className
        }
        title="Install Prestigious Hub on this device"
      >
        <Download className="w-3 h-3" />
        <span>Install Hub App</span>
      </button>

      {showHelp && (
        <InstallHelpDialog
          platform={platform}
          onClose={() => setShowHelp(false)}
        />
      )}
    </>
  );
}

function InstallHelpDialog({ platform, onClose }) {
  const isIOS = platform === 'ios';
  const isAndroid = platform === 'android';

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 px-4"
      data-testid="install-help-dialog"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl max-w-sm w-full p-5 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-3">
          <div>
            <h2 className="text-base font-bold text-slate-900">
              Install Prestigious Hub
            </h2>
            <p className="text-xs text-slate-500 mt-0.5">
              Add the Hub to your device so it opens like a native app.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-slate-400 hover:text-slate-700"
            data-testid="install-help-close"
            aria-label="Close install instructions"
          >
            <XIcon className="w-4 h-4" />
          </button>
        </div>

        {isIOS && (
          <ol
            className="text-sm text-slate-700 space-y-2"
            data-testid="install-help-ios"
          >
            <li className="flex items-start gap-2">
              <Share2 className="w-4 h-4 mt-0.5 text-slate-500 shrink-0" />
              <span>Tap the <b>Share</b> button in Safari.</span>
            </li>
            <li className="ml-6">Choose <b>Add to Home Screen</b>.</li>
            <li className="ml-6">Tap <b>Add</b> to confirm.</li>
            <li className="ml-6 text-xs text-slate-500">
              Use Safari — install is not available in other iOS browsers.
            </li>
          </ol>
        )}

        {isAndroid && (
          <ol
            className="text-sm text-slate-700 space-y-2"
            data-testid="install-help-android"
          >
            <li className="flex items-start gap-2">
              <MoreVertical className="w-4 h-4 mt-0.5 text-slate-500 shrink-0" />
              <span>Open your browser menu (⋮).</span>
            </li>
            <li className="ml-6">
              Choose <b>Install app</b> or <b>Add to Home screen</b>.
            </li>
            <li className="ml-6">Confirm the installation prompt.</li>
          </ol>
        )}

        {!isIOS && !isAndroid && (
          <ol
            className="text-sm text-slate-700 space-y-2"
            data-testid="install-help-desktop"
          >
            <li>
              Look for the <b>Install app</b> icon in your browser's
              address bar (right side).
            </li>
            <li>Or open the browser menu and choose <b>Install Prestigious Hub</b>.</li>
            <li className="text-xs text-slate-500">
              The install option is available in Chrome, Edge, and other
              Chromium-based browsers.
            </li>
          </ol>
        )}

        <div className="mt-4 flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="h-8 px-4 rounded-md bg-slate-900 text-white text-sm hover:bg-slate-800"
            data-testid="install-help-dismiss"
          >Close</button>
        </div>
      </div>
    </div>
  );
}
